import { Apartment } from "@boark/shared/types/apartment";
import { ApartmentWithHoldingsAndLeasesAndSublets } from "@boark/shared/types/apartment-with-holdings-and-leases-and-sublets";
import { Holding } from "@boark/shared/types/holding";
import { Lease } from "@boark/shared/types/lease";
import { Sublet } from "@boark/shared/types/sublet";
import {
  formatResidencyName,
  getResidencyWithinDate,
} from "@boark/shared/utils/apartment-residency";
import { Outlet, useSearchParams } from "react-router-dom";

import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { OwnerDropdown } from "../../components/owner-dropdown";
import { ResidencyName } from "../../components/residency-name";
import { Column, Filter, Table } from "../../components/table";
import { useAdditionalFields } from "../../hooks/addition-fields";
import { useGetApartments } from "../../queries/get-apartments";
import { formatApartmentLabel } from "../../services/apartment";
import { useLocale } from "../../stores/locale";
import { createIssueRouteForRecord } from "../../utils/create-issue-route";
import { formatPercentage } from "../../utils/format-percentage";
import { recordIdToId } from "../../utils/record-id-to-id";

export function Apartments() {
  const { locale: t, get: getLocale } = useLocale();
  const { data: apartments, isLoading: isApartmentsLoading } = useGetApartments();
  const { data: additionalFields } = useAdditionalFields("apartment");
  const [searchParams, setSearchParams] = useSearchParams();

  function updateSearchParams(params: Record<string, string>) {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...params,
    });
  }

  return (
    <>
      <Outlet />
      <div className="w-full min-h-full overflow-x-auto">
        <div className="flex justify-between items-center">
          <H1>{t.apartment.apartments}</H1>
          <Button route="/admin/register/apartment/create">
            {t.apartment.create}
          </Button>
        </div>
        <Table
          loading={isApartmentsLoading}
          showFieldSelector
          actions={(row: ApartmentData) => {
            return [
              ...(row.currentHolding
                ? [
                    {
                      label: getLocale("apartment.viewActiveHoldings"),
                      route: `/admin/register/holding/?filterBy=${encodeURIComponent(
                        JSON.stringify([
                          {
                            field: "apartment.index",
                            value: [row.index],
                            operator: "eq",
                          },
                          {
                            field: "active",
                            value: [true],
                            operator: "eq",
                          },
                        ]),
                      )}`,
                    },
                  ]
                : []),
              ...(row.holdings.length > 0
                ? [
                    {
                      label: getLocale("apartment.viewHoldings"),
                      route: `/admin/register/holding/?filterBy=${encodeURIComponent(
                        JSON.stringify([
                          {
                            field: "apartment.index",
                            value: [row.index],
                            operator: "eq",
                          },
                        ]),
                      )}`,
                    },
                  ]
                : []),
              ...(row.currentLease
                ? [
                    {
                      label: getLocale("apartment.viewActiveLeases"),
                      route: `/admin/register/lease/?filterBy=${encodeURIComponent(
                        JSON.stringify([
                          {
                            field: "apartment.index",
                            value: [row.index],
                            operator: "eq",
                          },
                          {
                            field: "active",
                            value: [true],
                            operator: "eq",
                          },
                        ]),
                      )}`,
                    },
                  ]
                : []),
              ...(row.leases.length > 0
                ? [
                    {
                      label: getLocale("apartment.viewLeases"),
                      route: `/admin/register/lease/?filterBy=${encodeURIComponent(
                        JSON.stringify([
                          {
                            field: "apartment.index",
                            value: [row.index],
                            operator: "eq",
                          },
                        ]),
                      )}`,
                    },
                  ]
                : []),
              ...(row.currentSublet
                ? [
                    {
                      label: getLocale("apartment.viewActiveSublets"),
                      route: `/admin/register/sublet/?filterBy=${encodeURIComponent(
                        JSON.stringify([
                          {
                            field: "apartment.index",
                            value: [row.index],
                            operator: "eq",
                          },
                          {
                            field: "active",
                            value: [true],
                            operator: "eq",
                          },
                        ]),
                      )}`,
                    },
                  ]
                : []),
              ...(row.holdings.some((holding) => holding.sublets.length > 0)
                ? [
                    {
                      label: getLocale("apartment.viewSublets"),
                      route: `/admin/register/sublet/?filterBy=${encodeURIComponent(
                        JSON.stringify([
                          {
                            field: "apartment.index",
                            value: [row.index],
                            operator: "eq",
                          },
                        ]),
                      )}`,
                    },
                  ]
                : []),
              {
                label: t.issue.create,
                route: createIssueRouteForRecord({
                  type: "apartment",
                  recordId: recordIdToId(row.id),
                  label: formatApartmentLabel(row),
                }),
              },
            ];
          }}
          data={formatApartments(apartments || [])}
          editRouteButton={(row) =>
            `/admin/register/apartment/${recordIdToId(row.id)}/edit`
          }
          filterBy={
            JSON.parse(searchParams.get("filterBy") || "[]") as Filter[]
          }
          locale={{ yes: t.yes, no: t.no, actions: t.action.actions, empty: t.empty }}
          removeRouteButton={(row) =>
            `/admin/register/apartment/${recordIdToId(row.id)}/delete`
          }
          selectedColumns={
            JSON.parse(
              searchParams.get("selectedColumns") || "[]",
            ) as string[]
          }
          sortBy={searchParams.get("sortBy") || "index"}
          sortByDirection={
            searchParams.get("sortByDirection") as "asc" | "desc"
          }
          viewRouteButton={(row) =>
            `/admin/register/apartment/${recordIdToId(row.id)}`
          }
          onFieldSelectorChange={(value) => {
            updateSearchParams({
              selectedColumns: JSON.stringify(value),
            });
          }}
          onFilter={(value: Filter[]) => {
            updateSearchParams({
              filterBy: JSON.stringify(value),
            });
          }}
          onSort={(field, direction) => {
            updateSearchParams({
              sortBy: field,
              sortByDirection: direction,
            });
          }}
        >
          <Column alwaysShow hideFilter field="index" filterableBy="text">
            #
          </Column>
          <Column alwaysShow field="objectNumber">
            {t.apartment.objectNumber}
          </Column>
          <Column field="streetNumber">{t.apartment.streetNumber}</Column>
          <Column field="apartmentNumber">
            {t.apartment.apartmentNumber}
          </Column>
          <Column
            field="floorNumber"
            filterableBy="select"
            filterOptions={() => [
              { label: "1", value: "1" },
              { label: "2", value: "2" },
              { label: "3", value: "3" },
              { label: "4", value: "4" },
              { label: "5", value: "5" },
            ]}
          >
            {t.apartment.floorNumber}
          </Column>
          <Column body={(row: Apartment) => `${row.area}m²`} field="area">
            {t.apartment.area}
          </Column>
          <Column
            alwaysShow
            body={(row: ApartmentData) => (
              <div className="flex gap-2 justify-between">
                {!row.hasResidents && <span>-</span>}
                {row.hasResidents && (
                  <>
                    <div className="whitespace-nowrap">
                      {row.currentResidency && (
                        <ResidencyName residency={row.currentResidency} />
                      )}
                      {row.currentSublet &&
                        row.currentSubletTenantNames !== "" && (
                          <>
                            {", "}
                            <span className="italic">
                              {getLocale("apartment.subletTo")}:{" "}
                              <ResidencyName residency={row.currentSublet} />
                            </span>
                          </>
                        )}
                    </div>
                    <OwnerDropdown
                      holding={row.currentHolding}
                      lease={row.currentLease}
                      sublet={row.currentSublet}
                    />
                  </>
                )}
              </div>
            )}
            field="currentResidentNames"
            filterableBy="text"
          >
            {getLocale("apartment.residents")}
          </Column>
          <Column
            alwaysShow
            body={(row: ApartmentData) => t.apartment.state[row.state]}
            field="state"
            filterableBy="select"
            filterOptions={() => [
              { label: t.apartment.state.vacant, value: "vacant" },
              { label: t.apartment.state.condo, value: "condo" },
              { label: t.apartment.state.lease, value: "lease" },
              { label: t.apartment.state.sublet, value: "sublet" },
            ]}
          >
            {t.apartment.state.state}
          </Column>
          <Column
            body={(row: Apartment) => formatPercentage(row.share ?? 0, 4)}
            field="share"
          >
            {t.apartment.share}
          </Column>
          <Column alwaysShow field="issueCount">
            {t.apartment.issueCount}
          </Column>
          {additionalFields.map((field) => (
            <Column key={field.name} field={`additionalData.${field.name}`}>
              {field.label}
            </Column>
          ))}
        </Table>
      </div>
    </>
  );
}

function formatApartments(
  apartments: ApartmentWithHoldingsAndLeasesAndSublets[],
): (ApartmentWithHoldingsAndLeasesAndSublets & ApartmentData)[] {
  return apartments.map((apartment) => getApartmentData(apartment));
}

type ApartmentData = ApartmentWithHoldingsAndLeasesAndSublets & {
  currentHolding?: Holding;
  currentLease?: Lease;
  currentSublet?: Sublet;
  currentResidency?: Holding | Lease;
  state: "vacant" | "condo" | "lease" | "sublet";
  hasResidents: boolean;
  currentResidentNames: string;
  currentSubletTenantNames: string;
};
function getApartmentData(
  apartment: ApartmentWithHoldingsAndLeasesAndSublets,
): ApartmentData {
  const now = new Date();

  const currentHolding = getResidencyWithinDate(apartment.holdings, now);

  const currentLease = getResidencyWithinDate(apartment.leases, now);

  let currentSublet: Sublet | undefined = undefined;
  if (currentHolding) {
    currentSublet = getResidencyWithinDate(currentHolding.sublets, now);
  } else if (currentLease) {
    currentSublet = getResidencyWithinDate(currentLease.sublets, now);
  }

  let state: "vacant" | "condo" | "lease" | "sublet" = "vacant";
  if (currentSublet) {
    state = "sublet";
  } else if (currentLease) {
    state = "lease";
  } else if (currentHolding) {
    state = "condo";
  }

  const currentResidents = currentHolding?.owners || currentLease?.tenants;

  const currentResidency = currentHolding ?? currentLease;
  const currentResidentNames = currentResidency
    ? formatResidencyName(currentResidency)
    : "";
  const currentSubletTenantNames = currentSublet
    ? formatResidencyName(currentSublet)
    : "";

  const hasResidents =
    (currentResidents !== undefined && currentResidents.length > 0) ||
    (currentSubletTenantNames !== undefined &&
      currentSubletTenantNames.length > 0);

  return {
    ...apartment,
    currentHolding,
    currentLease,
    currentSublet,
    currentResidency,
    state,
    hasResidents,
    currentResidentNames,
    currentSubletTenantNames,
  };
}
